import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../store/store";
import {EstateFlowchartState, EstateToggle} from "../model/EstateFlowchartTypes";
import {MemberGroup} from "../../ClientManagement/models/InvestorGroupType";
import {SideDrawerDetails} from "../components/SideDrawer/SideDrawer";
import {EstateCardViewModeType, EstateTaxDueType} from "../components/EstateCard/EstateCard";

export interface FlowchartState {
    flowcharts: EstateFlowchartState[];
    selectedFlowchartId: string | undefined;
    familyMembers: MemberGroup;
    listOfStates: any;
    isSideDrawerOpen: boolean;
    sideDrawerDetails: SideDrawerDetails | undefined;
    genericErrorModal?: {
        isOpen: boolean;
        operationId: string;
    };
    isClientDocumentsOpen: boolean;
    selectedFlowchartIdAfterResourcesNav?: string;
}

export const initialState: FlowchartState = {
    flowcharts: [],
    selectedFlowchartId: undefined,
    familyMembers: {} as MemberGroup,
    listOfStates: {},
    isSideDrawerOpen: false,
    sideDrawerDetails: undefined,
    genericErrorModal: undefined,
    isClientDocumentsOpen: false,
    selectedFlowchartIdAfterResourcesNav: undefined
}

const estateFlowchartSlice = createSlice({
    name: "estateFlowchart",
    initialState,
    reducers: {
        setEstateFlowchartState: (_state, action: PayloadAction<Array<EstateFlowchartState>>) => {
            _state.flowcharts = action.payload;
        },
        resetEstateFlowchartState: (_state) => {
            _state.flowcharts = [];
        },
        resetFlowchartState: (_state) => {
            _state.flowcharts = initialState.flowcharts;
            _state.selectedFlowchartId = initialState.selectedFlowchartId;
            _state.familyMembers = initialState.familyMembers;
            _state.listOfStates = initialState.listOfStates;
            _state.isSideDrawerOpen = initialState.isSideDrawerOpen;
            _state.sideDrawerDetails = initialState.sideDrawerDetails;
            _state.selectedFlowchartIdAfterResourcesNav = initialState.selectedFlowchartIdAfterResourcesNav;
        },
        resetFlowchartStateAfterResourcesNav: (_state) => {
            _state.selectedFlowchartIdAfterResourcesNav = _state.selectedFlowchartId;

            _state.flowcharts = initialState.flowcharts;
            _state.selectedFlowchartId = initialState.selectedFlowchartId;
            _state.familyMembers = initialState.familyMembers;
            _state.listOfStates = initialState.listOfStates;
            _state.isSideDrawerOpen = initialState.isSideDrawerOpen;
            _state.sideDrawerDetails = initialState.sideDrawerDetails;
        },
        updateEstateFlowchart: (_state, action: PayloadAction<EstateFlowchartState>) => {
            const newFlowchart = action.payload;
            const index = _state.flowcharts.findIndex((flowchart) => flowchart.flowchartId === newFlowchart.flowchartId);
            if (index !== -1) {
                _state.flowcharts[index] = newFlowchart;
                newFlowchart.viewMode = _state.flowcharts[index].viewMode;
                newFlowchart.estateTaxDueTypes = _state.flowcharts[index].estateTaxDueTypes;
            } else {
                newFlowchart.viewMode = EstateCardViewModeType.SIMPLE;
                newFlowchart.estateTaxDueTypes = [];
                _state.flowcharts.push(newFlowchart);
            }
            _state.selectedFlowchartId = newFlowchart.flowchartId;
        },
        deleteEstateFlowchart: (_state, action: PayloadAction<string>) => {
            const flowchartIdToDelete = action.payload;
            if (_state.selectedFlowchartId === flowchartIdToDelete) {
                const index = _state.flowcharts.findIndex(fc => fc.flowchartId === flowchartIdToDelete);
                if (_state.flowcharts.length > 1 && index !== -1 && index < _state.flowcharts.length - 1) {
                    _state.selectedFlowchartId = _state.flowcharts[index + 1].flowchartId;
                } else if (_state.flowcharts.length > 1 && index > 0) {
                    _state.selectedFlowchartId = _state.flowcharts[index - 1].flowchartId;
                } else {
                    _state.selectedFlowchartId = undefined;
                }
            }
            _state.flowcharts = _state.flowcharts.filter(flowchart => flowchart.flowchartId !== flowchartIdToDelete);
        },
        deleteRevocableTrust: (_state, action: PayloadAction<{ flowchartId: string; trustId: string; }>) => {
            const {flowchartId, trustId} = action.payload;
            const flowchart = _state.flowcharts.find(fc => fc.flowchartId === flowchartId);
            if (flowchart && flowchart.revocableTrust?.trustId === trustId) {
                flowchart.revocableTrust = null;
                if (flowchart.estateToggle === EstateToggle.IN_ESTATE && (!flowchart.willSummary?.primaryWill && !flowchart.willSummary?.partnerWill)) {
                    flowchart.estateToggle = EstateToggle.FULL_ESTATE;
                }
            }
        },
        deleteIrrevocableTrust: (_state, action: PayloadAction<{ flowchartId: string; trustId: string; }>) => {
            const {flowchartId, trustId} = action.payload;
            const index = _state.flowcharts.findIndex(fc => fc.flowchartId === flowchartId);
            if (index !== -1 && Array.isArray(_state.flowcharts[index].irrevocableTrusts)) {
                const updatedIrrevocableTrusts = _state.flowcharts[index].irrevocableTrusts?.filter(irrevocableTrust => irrevocableTrust.trustId !== trustId) || [];
                _state.flowcharts[index] = {
                    ..._state.flowcharts[index],
                    irrevocableTrusts: updatedIrrevocableTrusts
                };
                if (
                    updatedIrrevocableTrusts.length === 0 &&
                    _state.flowcharts[index].estateToggle === EstateToggle.OUT_OF_ESTATE
                ) {
                    _state.flowcharts[index].estateToggle = EstateToggle.FULL_ESTATE;
                }
            }
        },
        deleteMiscellaneousTrust: (_state, action: PayloadAction<{ flowchartId: string; trustId: string; }>) => {
            const {flowchartId, trustId} = action.payload;
            const index = _state.flowcharts.findIndex(fc => fc.flowchartId === flowchartId);
            if (index !== -1 && Array.isArray(_state.flowcharts[index].wildcardTrusts)) {
                const updatedMiscellaneousTrusts = _state.flowcharts[index].wildcardTrusts?.filter(wildcard => wildcard.trustId !== trustId);
                _state.flowcharts[index].wildcardTrusts = updatedMiscellaneousTrusts;
            }
        },
        deleteWill: (_state, action: PayloadAction<{ flowchartId: string; trustId: string }>) => {
            const {flowchartId, trustId} = action.payload;
            const index = _state.flowcharts.findIndex(fc => fc.flowchartId === flowchartId);
            if (index !== -1 && _state.flowcharts[index].willSummary) {
                // Determine which will is being deleted
                const isPrimaryWill = _state.flowcharts[index].willSummary?.primaryWill?.trustId === trustId;
                const isPartnerWill = _state.flowcharts[index].willSummary?.partnerWill?.trustId === trustId;
                const noOtherWill =
                    (isPrimaryWill && !_state.flowcharts[index].willSummary?.partnerWill) ||
                    (isPartnerWill && !_state.flowcharts[index].willSummary?.primaryWill);
                const shouldSetFullEstate =
                    _state.flowcharts[index].estateToggle === EstateToggle.IN_ESTATE &&
                    !_state.flowcharts[index].revocableTrust &&
                    noOtherWill;

                _state.flowcharts[index] = {
                    ..._state.flowcharts[index],
                    willSummary: {
                        primaryWill: _state.flowcharts[index].willSummary?.primaryWill?.trustId === trustId ? undefined : _state.flowcharts[index].willSummary?.primaryWill,
                        partnerWill: _state.flowcharts[index].willSummary?.partnerWill?.trustId === trustId ? undefined : _state.flowcharts[index].willSummary?.partnerWill
                    }
                };
                if (shouldSetFullEstate) {
                    _state.flowcharts[index].estateToggle = EstateToggle.FULL_ESTATE;
                }
            }
        },
        setSelectedFlowchartId: (_state, action: PayloadAction<string | undefined>) => {
            _state.selectedFlowchartId = action.payload;
        },
        setFamilyMembers: (_state, action: PayloadAction<MemberGroup>) => {
            _state.familyMembers = action.payload;
        },
        setListOfStates: (_state, action: PayloadAction<any>) => {
            _state.listOfStates = action.payload;
        },
        deletePowerOfAppointment: (_state, action: PayloadAction<{
            flowchartId: string;
            trustId: string;
            powerId: string
        }>) => {
            const {flowchartId, trustId, powerId} = action.payload;
            const flowchart = _state.flowcharts.find(fc => fc.flowchartId === flowchartId);
            if (flowchart?.revocableTrust?.trustId === trustId && flowchart?.revocableTrust?.beneficiaries) {
                flowchart.revocableTrust.beneficiaries.firstDeathDistributions?.forEach((distribution: any) => {
                    if (Array.isArray(distribution.powersOfAppointment)) {
                        distribution.powersOfAppointment = distribution.powersOfAppointment.filter(
                            (power: any) => power.poaId !== powerId
                        );
                    }
                });
                flowchart.revocableTrust.beneficiaries.remainderDistributions?.forEach((distribution: any) => {
                    if (Array.isArray(distribution.powersOfAppointment)) {
                        distribution.powersOfAppointment = distribution.powersOfAppointment.filter(
                            (power: any) => power.poaId !== powerId
                        );
                    }
                });
            }
        },
        setSideDrawerOpen: (_state, action: PayloadAction<boolean>) => {
            _state.isSideDrawerOpen = action.payload;
        },
        setSideDrawerDetails: (_state, action: PayloadAction<SideDrawerDetails | undefined>) => {
            _state.sideDrawerDetails = action.payload;
        },
        setViewMode: (_state, action: PayloadAction<EstateCardViewModeType>) => {
            const selectedId = _state.selectedFlowchartId;
            if (!selectedId) return;
            const flowchart = _state.flowcharts.find(fc => fc.flowchartId === selectedId);
            if (flowchart) {
                flowchart.viewMode = action.payload;
            }
        },
        setEstateToggle: (_state, action: PayloadAction<{ toggle: EstateToggle }>) => {
            const { toggle } = action.payload;
            const flowchart = _state.flowcharts.find(fc => fc.flowchartId === _state.selectedFlowchartId);
            if (flowchart) {
                flowchart.estateToggle = toggle;
            }
        },
        setEstateTaxDueTypes: (_state, action: PayloadAction<EstateTaxDueType[]>) => {
            const selectedId = _state.selectedFlowchartId;
            if (!selectedId) return;
            const flowchart = _state.flowcharts.find(fc => fc.flowchartId === selectedId);
            if (flowchart) {
                flowchart.estateTaxDueTypes = action.payload;
            }
        },
        setGenericErrorModal: (_state, action: PayloadAction<{ isOpen: boolean; operationId: string }>) => {
            _state.genericErrorModal = action.payload;
        },
        setIsClientDocumentsOpen: (_state, action: PayloadAction<boolean>) => {
            _state.isClientDocumentsOpen = action.payload;
        },
        setSelectedFlowchartIdAfterResourcesNav: (_state, action: PayloadAction<string | undefined>) => {
            _state.selectedFlowchartIdAfterResourcesNav = action.payload;
        }
    }
});

export const estateToggleSelector = (state: RootState) : EstateToggle => {
    const selectedFlowchartId = state.estateFlowchart.estateFlowcharts.selectedFlowchartId;
    if (!selectedFlowchartId) return EstateToggle.FULL_ESTATE;
    const flowchart = state.estateFlowchart.estateFlowcharts.flowcharts.find(fc => fc.flowchartId === selectedFlowchartId);
    return flowchart?.estateToggle ?? EstateToggle.FULL_ESTATE;
};
export const getEstateFlowchartsSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.flowcharts;
export const selectedFlowchartIdSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.selectedFlowchartId;
export const getSelectedFlowchart = (state: RootState) => {
    const selectedFlowchartId = state.estateFlowchart.estateFlowcharts.selectedFlowchartId;
    if (!selectedFlowchartId) {
        return undefined;
    }
    return state.estateFlowchart.estateFlowcharts.flowcharts.find(fc => fc.flowchartId === selectedFlowchartId);
}

export const selectRevTrust = (state: RootState) => {
    const flowchart = getSelectedFlowchart(state);
    if (!flowchart) {
        return undefined;
    }
    return flowchart.revocableTrust;
}

export const familyMembersSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.familyMembers;
export const listOfStatesSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.listOfStates;

export const sideDrawerOpenStateSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.isSideDrawerOpen;
export const sideDrawerDetailsSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.sideDrawerDetails;
export const genericErrorModalSelector = (state: RootState) => state.estateFlowchart.estateFlowcharts.genericErrorModal;
export const selectIsClientDocumentsOpen = (state: RootState) =>
    state.estateFlowchart.estateFlowcharts.isClientDocumentsOpen;
export const selectFlowchartIdAfterResourcesNav = (state: RootState) =>
    state.estateFlowchart.estateFlowcharts.selectedFlowchartIdAfterResourcesNav;



export const {
    setEstateFlowchartState,
    resetEstateFlowchartState,
    resetFlowchartState,
    updateEstateFlowchart,
    deleteEstateFlowchart,
    deleteRevocableTrust,
    deleteIrrevocableTrust,
    deleteMiscellaneousTrust,
    deleteWill,
    setSelectedFlowchartId,
    setFamilyMembers,
    setListOfStates,
    deletePowerOfAppointment,
    setSideDrawerOpen,
    setSideDrawerDetails,
    setViewMode,
    setEstateToggle,
    setGenericErrorModal,
    setEstateTaxDueTypes,
    setIsClientDocumentsOpen,
    resetFlowchartStateAfterResourcesNav,
    setSelectedFlowchartIdAfterResourcesNav
} = estateFlowchartSlice.actions

export default estateFlowchartSlice.reducer;